/**
 * @file ApiService.ts
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import axios, { AxiosError } from "axios";
import { ApiError } from "../models/ApiError";
import { BaseService } from "../BaseService";
import { Api } from "./Api";
import { GetPatientResponse } from "../models/GetPatientResponse";
import { ViewQrResponse } from "../models/ViewQrResponse";
import { AppAccessToken } from "../models/AppAccessToken";
import { GetClinicResponse } from "../models/GetClinicResponse";
import { CreatePatientResponse } from "../models/CreatePatientResponse";
import { GetAdminResponse } from "../models/GetAdminResponse";
import { GetUserDetailResponse } from "../models/GetUserDetailResponse";
import { AdminRequest } from "../models/AdminRequest";
import { ClinicRequet } from "../models/ClinicRequet";
import { Consent } from "../models/Consent";
import { UserConsentRequest } from "../models/UserConsentRequest";
import { GenericResponse } from "../models/GenericResponse";
import { AppExiosErros } from "../../components/utils/AppConstant";

/**
 * The interface definition for the TempPassword
 */
export interface TempPassword {
    rawPassword: string;
    encodedPassword: string
}

/**
 * The enum definition for Method
 */
enum Method {
    POST = 'POST',
    GET = 'GET',
    PUT = "PUT",
    DELETE = 'DELETE'
}

/**
 * The class definition for the ApiService
 */
export class ApiService extends BaseService implements Api {

    private host = '';
    private relyStateURL = '';
    constructor() {
        super()

        this.logger.verbose(`${process.env.REACT_APP_SERVER_HOST}`, ApiService.name);
        this.logger.verbose(`${process.env.REACT_APP_RELAY_STATE}`, ApiService.name);

        if (process.env.REACT_APP_SERVER_HOST)
            this.host = process.env.REACT_APP_SERVER_HOST;
        if (process.env.REACT_APP_RELAY_STATE)
            this.relyStateURL = process.env.REACT_APP_RELAY_STATE;
    }

    /**
     * Calls login API
     * @param username string
     * @param password string
     * @returns 
     */
    async login(username: string, password: string): Promise<AppAccessToken> {
        const requestData = {
            username: `Anonymous:${username}`,
            password: password,
            "grantType": "password"

        }
        const response = await this.callAPI(Method.POST, 'auth/identity/token/oauth', requestData);
        return response.data as AppAccessToken;
    }

    /**
     * Creates a patient
     * @param patient any
     * @param accessToken string 
     * @returns 
     */
    async createPatient(patient: any, accessToken: string): Promise<CreatePatientResponse> {
        const response = await this.callAPI(Method.POST, 'patient', patient, accessToken);
        return response.data as CreatePatientResponse;
    }

    /**
     * Updates a patient
     * @param patient any
     * @param accessToken string 
     * @returns 
     */
    async updatePatient(patient: any, accessToken: string): Promise<GenericResponse> {
        const response = await this.callAPI(Method.POST, 'patient/identifier', patient, accessToken);
        return response.data as GenericResponse;
    }

    /**
     * Creates an admin
     * @param adminRequest AdminRequest
     * @param isBSCUser Boolean
     * @param accessToken string
     * @returns ApiError
     */
    async createAdmin(adminRequest: AdminRequest, isBSCUser: boolean, accessToken: string) {
        const url = isBSCUser ? 'admin/bsc-rep/invite' : 'admin/hcp';

        const  response = await this.callAPI(Method.POST, url, adminRequest, accessToken);
        return response.data;
    }

    /**
     * Creates a clinic
     * @param clinicRequest ClinicRequet
     * @param accessToken string
     * @returns 
     */
    async createClinic(clinicRequest: ClinicRequet, accessToken: string) {
        const  response = await this.callAPI(Method.POST, 'clinic', clinicRequest, accessToken);
        return response.data;
    }

    /**
     * Edits a clinic
     * @param name string
     * @param clinicId string
     * @param accessToken string
     * @returns ApiError
     */
    async editClinic(name: string, clinicId: string, accessToken: string) {
        const requestData = {
            name: name
        }
        const  response = await this.callAPI(Method.PUT,  `clinic/${clinicId}`, requestData, accessToken);
        return response.data;
    }    
    
    /**
     * Resends an invite to admin
     * @param adminRequest AdminRequest
     * @param isBSCUser Boolean
     * @param accessToken string
     * @returns ApiError
     */
    async resendInvite(id: string, mailId: string, accessToken: string) {
        const requestData = {
            clinicId: id,
            email: mailId
        }
        const  response = await this.callAPI(Method.POST, 'admin/re/invite', requestData, accessToken);
        return response.data;
    }    

    /**
     * Calls a view QR code API
     * @param patientId string
     * @param accessToken string
     * @returns 
     */
    async viewQR(patientId: string, accessToken: string): Promise<ViewQrResponse> {
        const userData = JSON.stringify({
            id: patientId
        });
        const response = await this.callAPI(Method.POST, 'patient/view-qr', userData, accessToken);
        const viewQrResponse: ViewQrResponse = response.data;

        return viewQrResponse;
    }

    /**
     * Gets the list of the patients
     * @param clinicId string
     * @param accessToken string
     * @returns 
     */
    async getPatientList(clinicId: string, accessToken: string, page: number): Promise<GetPatientResponse> {
        const response = await this.callAPI(Method.GET, `patient/${clinicId}?page=${page}`, {}, accessToken);
        const getPatientResponse: GetPatientResponse = response.data;

        return getPatientResponse;
    }

    /**
     * Gets the list of the clinic
     * @param accessToken string
     * @returns 
     */
    async getClinicList(accessToken: string): Promise<GetClinicResponse> {
        const response = await this.callAPI(Method.GET, `clinic`, {}, accessToken);
        const getClinicResponse: GetClinicResponse = response.data;

        return getClinicResponse;
    }

    /**
     * Gets admin list
     * @param clinicId string
     * @param accessToken string
     * @returns 
     */
    async getAdminList(clinicId: string, accessToken: string): Promise<GetPatientResponse> {
        const response = await this.callAPI(Method.GET, `admin/${clinicId}`, {}, accessToken);

        if(response instanceof ApiError){
            return Promise.reject(response as ApiError);
        }
        const getAdminResponse: GetAdminResponse = response.data;

        return getAdminResponse;
    }

    /**
     * Gets current user details
     * @param accessToken 
     * @returns 
     */
    async getMe(accessToken: string): Promise<GetUserDetailResponse> {
        const response = await this.callAPI(Method.GET, 'admin/me/info', {}, accessToken);
        const getUserDetailResponse: GetUserDetailResponse = response.data;

        return getUserDetailResponse;
    }

    /**
     * Logout user
     * @param accessToken 
     * @returns 
     */
    async logout(accessToken: string): Promise<any> {
        const response = await this.callAPI(Method.POST, 'auth/logout', {}, accessToken);
        const logoutResponse = response.data;

        return logoutResponse;
    }

    /**
     * Gets consents
     * @param accessToken 
     * @returns 
     */
    async getConsents(accessToken: string, query: any): Promise<Consent[]> {

        let queryBuilder: string = '';
        Object.keys(query).forEach(key => {
            queryBuilder += `${key}=${query[key]}`;
        }); 
        const response = await this.callAPI(Method.GET, `consents?${queryBuilder}`, undefined, accessToken);
        const consentResponse: Consent [] = response.data;

        return consentResponse;
    }

    /**
     * Gets user's consents
     * @param accessToken 
     * @returns 
     */
    async getUserConsents(accessToken: string, query: any): Promise<Consent[]> {
        let queryBuilder: string = '';
        Object.keys(query).forEach(key => {
            queryBuilder += `${key}=${query[key]}`;
        }); 
        const response = await this.callAPI(Method.GET, `consents/user/consents?${queryBuilder}`, undefined, accessToken);
        const consentResponse: Consent [] = response.data;

        return consentResponse;
    }

    /**
     * Updates user's consents
     * @param accessToken 
     * @returns 
     */
    async updateUserConsents(accessToken: string, userConsent: UserConsentRequest): Promise<GenericResponse> {
        const userData = JSON.stringify(userConsent);
        const response = await this.callAPI(Method.POST, 'consents/user/consents', userData, accessToken);
        const consentResponse: GenericResponse = response.data;

        return consentResponse;
    }    
    /**
     * Sets up
     * @param accessToken string
     * @param newPassword string
     * @returns 
     */
    async setNewPassword(accessToken: string, newPassword: string): Promise<any | ApiError> {
        const userData = JSON.stringify({ tempPassword: newPassword });
        const response = await this.callAPI(Method.POST, 'admin/set-password', userData, accessToken);

        return response.data;
    }

    /**
     * Deletes a patient API
     * @param patientId string
     * @param accessToken string
     * @returns 
     */
    async deletePatient(patientId: string, accessToken: string): Promise<any | ApiError> {
        const userData = JSON.stringify({ id: patientId });
        const response = await this.callAPI(Method.DELETE, 'patient', userData, accessToken);

        return response.data;
    }

    /**
     * Deletes an Admin API
     * @param email string
     * @param accessToken string
     * @returns 
     */
    async deleteAdmnin(email: string, clinicId: string, accessToken: string): Promise<any | ApiError> {
        const userData = JSON.stringify({ email: email,
                                          clinicId: clinicId });
        const response = await this.callAPI(Method.DELETE, 'admin', userData, accessToken);

        return response.data;
    }

    /**
     * Sends a reset password email
     * @param targetEmail string
     * @returns 
     */
    async sendResetPasswordEmail(targetEmail: string): Promise<any | ApiError> {
        const userData = JSON.stringify({ email: targetEmail });
        const response = await this.callAPI(Method.POST, 'admin/forgot-password', userData);

        return response.data;
    }

    /**
     * Gets an authenticate route
     * @returns 
     */
    async getAuthenticationRoute(): Promise<string> {
        const data = { relayState: this.relyStateURL };
        const response = await this.callAPI(Method.POST, 'auth/identity/token/saml/sso', data);

        return response.data;
    }

    /**
     * Generic method to call APIs
     * @param configMethod Method
     * @param partUrl string
     * @param requestData any
     * @param accessToken string
     * @returns 
     */
    private async callAPI(configMethod: Method, partUrl: string, requestData: any, accessToken?: string) {
        try {
            this.logger.verbose("callAPI()", ApiService.name);
            let configHeaders = {
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'Expires': '0'
            };
            if (accessToken) {
                configHeaders = {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache', // Pragma: no-cache is required for iOS Safari to behave correctly,
                    'Expires': '0'
                } as any;
            }
            this.logger.verbose(JSON.stringify(configHeaders), ApiService.name);
            const config = {
                method: configMethod,
                url: `${this.host}/${partUrl}`,
                headers: configHeaders,
                data: requestData,
                timeout: 10*1000 // In milliseconds
            };
            this.logger.verbose(JSON.stringify(config), ApiService.name);
            const response = await axios(config);
            if (Object.keys(response.data).includes('error') || Object.keys(response.data).includes('errorCode')  || (Object.keys(response.data).includes('code') && Object.keys(response.data).includes('type'))) {
                const apiError = new ApiError(response.data.error_description);
                Object.assign(apiError, response.data);
                throw apiError;
            }
            this.logger.verbose(JSON.stringify(response), ApiService.name);
            return response;
        } catch (error: unknown) {
            this.logger.error(JSON.stringify(error), ApiService.name);
            if(error instanceof AxiosError && (error as AxiosError).response?.status === 401) {
                this.logger.error(`..Broadcasting invalid credentials error.. ${(error as AxiosError).response?.status}`, ApiService.name);
                this.setIsLoggedIn(false);
            }
            if((error as any)?.response?.status === 400) {
                this.logger.error(`..Parsing validation Error.. ${(error as AxiosError).response?.status}`, ApiService.name);
                const apiError = (error as any).response!!.data as ApiError; // eslint-disable-line
                throw apiError;            
            }

            if (error instanceof ApiError) {
                throw error;
            }
            if (axios.isAxiosError(error) && error.response) {
                const axiosError = error as AxiosError;
                const apiError = error.response.data as ApiError;
                if(AppExiosErros.get(axiosError.code!))
                    apiError.message = AppExiosErros.get(axiosError.code!);
                this.logger.error(JSON.stringify(apiError), ApiService.name);
                throw apiError;
            }

            if (axios.isAxiosError(error)) {
                const axiosError = error as AxiosError;
                const apiError = new ApiError(axiosError.message);
                if(AppExiosErros.get(axiosError.code!))
                    apiError.message = AppExiosErros.get(axiosError.code!);
                this.logger.error(JSON.stringify(apiError), ApiService.name);
                throw apiError;
            }
            throw new ApiError("Unknown Error");
        }
    }
}